import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { auth, db } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDocs, collection, query, orderBy } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { useT } from '../../utils/useTranslation';
import { useLanguage } from '../../context/LanguageContext';
import HeroBackground from '../../components/ui/HeroBackground';
import ajrakTexture from '../../assets/ajrak_texture_new.jpg';
import { EDUCATION_OPTIONS } from '../admin/seedConstants';

export default function RegisterPage() {
  const navigate = useNavigate();
  const t = useT();
  const { language } = useLanguage();
  const isRTL = language === 'sindhi';

  const [branches, setBranches] = useState([]);
  const [literaryFields, setLiteraryFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [form, setForm] = useState({
    full_name: '', full_name_sindhi: '',
    literary_name: '', literary_name_sindhi: '',
    profession: '', profession_sindhi: '',
    date_of_birth: '',
    place_of_birth: '', place_of_birth_sindhi: '',
    education: [],
    literary_fields: [],
    literary_services: '', literary_services_sindhi: '',
    temporary_address: '', permanent_address: '',
    phone: '', email: '', password: '',
    branchId: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const brSnap = await getDocs(collection(db, 'branches'));
        setBranches(brSnap.docs.map(d => ({ _id: d.id, ...d.data() })));
        const flSnap = await getDocs(query(collection(db, 'literary-fields'), orderBy('name')));
        setLiteraryFields(flSnap.docs.map(d => ({ _id: d.id, ...d.data() })));
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleEdu = val => {
    set('education', form.education.includes(val) ? form.education.filter(v => v !== val) : [...form.education, val]);
  };
  const toggleLF = val => {
    set('literary_fields', form.literary_fields.includes(val) ? form.literary_fields.filter(v => v !== val) : [...form.literary_fields, val]);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!agreed) { toast.error(isRTL ? 'مهرباني ڪري شرطن سان اتفاق ڪريو' : 'Please agree to the constitution'); return; }
    if (form.literary_fields.length === 0) { toast.error(isRTL ? 'ادبي صنف چونڊيو' : 'Select at least one literary field'); return; }
    setLoading(true);
    try {
      const { user } = await createUserWithEmailAndPassword(auth, form.email, form.password);
      const branch = branches.find(b => b._id === form.branchId);

      // Generate Recovery Key
      const recoveryKey = Math.random().toString(36).substring(2, 8).toUpperCase() + '-' + Math.random().toString(36).substring(2, 8).toUpperCase();

      // Save user doc
      await setDoc(doc(db, 'users', user.uid), {
        displayName: form.full_name, displayNameSindhi: form.full_name_sindhi,
        email: form.email, role: 'member', branchId: form.branchId,
        districtId: branch?.districtId || '', memberId: '', login_disabled: false,
        status: 'pending', recovery_key: recoveryKey, createdAt: new Date().toISOString()
      });

      // Save member doc
      const memberRef = doc(collection(db, 'members'));
      await setDoc(memberRef, {
        full_name: form.full_name, full_name_sindhi: form.full_name_sindhi,
        literary_name: form.literary_name, literary_name_sindhi: form.literary_name_sindhi,
        profession: form.profession, profession_sindhi: form.profession_sindhi,
        date_of_birth: form.date_of_birth,
        place_of_birth: form.place_of_birth, place_of_birth_sindhi: form.place_of_birth_sindhi,
        education: form.education,
        literary_fields: form.literary_fields,
        literary_services: form.literary_services, literary_services_sindhi: form.literary_services_sindhi,
        temporary_address: form.temporary_address, permanent_address: form.permanent_address,
        contact_info: { phone: form.phone, email: form.email },
        current_branch_id: form.branchId, districtId: branch?.districtId || '',
        userId: user.uid, status: 'pending', membership_status: 'Temporary',
        agreed_to_constitution: true, createdAt: new Date().toISOString()
      });

      // Link member to user
      await setDoc(doc(db, 'users', user.uid), { memberId: memberRef.id }, { merge: true });

      setRegisteredKey(recoveryKey);
      setShowSuccessModal(true);
      toast.success(t('registrationSuccess'));
    } catch (err) {
      console.error(err);
      toast.error(err.code === 'auth/email-already-in-use' ? (isRTL ? 'اي ميل اڳ ۾ ئي موجود آهي' : 'Email already in use') : err.message);
    } finally { setLoading(false); }
  };

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredKey, setRegisteredKey] = useState('');

  const labelStyle = { fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' };
  const sindhiInput = { fontFamily: 'var(--font-sindhi)', direction: 'rtl', textAlign: 'right' };

  return (
    <div>
      <Navbar />
      <div className="hero-premium" style={{ minHeight: 400, textAlign: 'center', color: 'white' }}>
        <HeroBackground />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 56 : 48, fontWeight: 800, marginBottom: 16 }}>
            {isRTL ? 'ميمبرشپ فارم' : 'Membership Form'}
          </h1>
          <p style={{ opacity: 0.8, fontSize: isRTL ? 20 : 16, maxWidth: 700, margin: '0 auto', lineHeight: 1.6, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {isRTL ? 'ڏسو "سنڌ سنگت" جي 1998ع واري پيٽي قاعدن جو فقرو_3' : 'See Clause 3 of the 1998 bylaws of "Sindh Sangat."'}
          </p>
        </motion.div>
      </div>

      <div style={{ background: 'var(--sindhi-ivory)', backgroundImage: `url(${ajrakTexture})`, backgroundSize: '600px', backgroundRepeat: 'repeat', position: 'relative', zIndex: 10, marginTop: '-80px' }}>
        <div style={{ position: 'absolute', inset: 0, backdropFilter: 'blur(3px)', background: 'rgba(253,248,235,0.7)', zIndex: 0, pointerEvents: 'none' }} />
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px 100px', position: 'relative', zIndex: 1 }}>
          <Card style={{ padding: '48px 56px', background: 'white', border: '1px solid var(--sindhi-gold-muted)', borderRadius: 24, boxShadow: '0 40px 100px -20px rgba(0,0,0,0.1)', direction: isRTL ? 'rtl' : 'ltr' }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, textAlign: isRTL ? 'right' : 'left' }}>

                {/* 1. Full Name */}
                <div className="form-group">
                  <label className="form-label">{isRTL ? 'نالو پورو زات سميت (انگريزي)' : 'Full Name With Surname (English)'} *</label>
                  <input className="form-input" placeholder="e.g. Ahmed Ali Soomro" value={form.full_name} onChange={e => set('full_name', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="form-label" style={sindhiInput}>{isRTL ? 'نالو پورو زات سميت (سنڌي)' : 'Full Name (Sindhi)'} *</label>
                  <input className="form-input" style={sindhiInput} placeholder="مثال: احمد علي سومرو" value={form.full_name_sindhi} onChange={e => set('full_name_sindhi', e.target.value)} required />
                </div>

                {/* 2. Literary Name */}
                <div className="form-group">
                  <label className="form-label">{isRTL ? 'ادبي نالو (انگريزي)' : 'Literary Name (English)'}</label>
                  <input className="form-input" placeholder="e.g. Ahmed Adabi" value={form.literary_name} onChange={e => set('literary_name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={sindhiInput}>{isRTL ? 'ادبي نالو (سنڌي)' : 'Literary Name (Sindhi)'}</label>
                  <input className="form-input" style={sindhiInput} placeholder="مثال: احمد ادبي" value={form.literary_name_sindhi} onChange={e => set('literary_name_sindhi', e.target.value)} />
                </div>

                {/* 3. Profession */}
                <div className="form-group">
                  <label className="form-label">{isRTL ? 'ڪرت (انگريزي)' : 'Profession (English)'}</label>
                  <input className="form-input" placeholder="e.g. Teacher" value={form.profession} onChange={e => set('profession', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={sindhiInput}>{isRTL ? 'ڪرت (سنڌي)' : 'Profession (Sindhi)'}</label>
                  <input className="form-input" style={sindhiInput} placeholder="مثال: استاد" value={form.profession_sindhi} onChange={e => set('profession_sindhi', e.target.value)} />
                </div>

                {/* 4. Age / DOB */}
                <div className="form-group">
                  <label className="form-label" style={labelStyle}>{isRTL ? 'عمر / ڄمڻ جي تاريخ' : 'Age / Date of Birth'} *</label>
                  <input className="form-input" type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} required />
                </div>

                {/* 5. Place of Birth */}
                <div className="form-group">
                  <label className="form-label">{isRTL ? 'ڄم جو ھنڌ' : 'Place of Birth'}</label>
                  <input className="form-input" placeholder={isRTL ? "شهر / ڳوٺ" : "City / Village"} value={form.place_of_birth} onChange={e => set('place_of_birth', e.target.value)} />
                </div>

                {/* 6. Education - Multi-select checkboxes */}
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label" style={labelStyle}>{isRTL ? 'تعليمي لياقتون' : 'Education'}</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                    {EDUCATION_OPTIONS.map(opt => (
                      <label key={opt.value} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 12, cursor: 'pointer',
                        background: form.education.includes(opt.value) ? 'var(--sindhi-maroon)' : 'var(--sindhi-ivory)',
                        color: form.education.includes(opt.value) ? 'white' : 'var(--sindhi-navy)',
                        border: `1px solid ${form.education.includes(opt.value) ? 'var(--sindhi-maroon)' : 'var(--sindhi-cream)'}`,
                        fontWeight: 700, fontSize: 13, transition: 'all 0.2s', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
                      }}>
                        <input type="checkbox" checked={form.education.includes(opt.value)} onChange={() => toggleEdu(opt.value)} style={{ display: 'none' }} />
                        {isRTL ? opt.sd : opt.en}
                      </label>
                    ))}
                  </div>
                </div>

                {/* 7. Literary Field - from admin panel */}
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label" style={labelStyle}>{isRTL ? 'ادب جي صنف' : 'Literary Field'} *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                    {literaryFields.map(f => (
                      <label key={f._id} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 12, cursor: 'pointer',
                        background: form.literary_fields.includes(f._id) ? 'var(--sindhi-navy)' : 'var(--sindhi-ivory)',
                        color: form.literary_fields.includes(f._id) ? 'white' : 'var(--sindhi-navy)',
                        border: `1px solid ${form.literary_fields.includes(f._id) ? 'var(--sindhi-navy)' : 'var(--sindhi-cream)'}`,
                        fontWeight: 700, fontSize: 13, transition: 'all 0.2s', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
                      }}>
                        <input type="checkbox" checked={form.literary_fields.includes(f._id)} onChange={() => toggleLF(f._id)} style={{ display: 'none' }} />
                        {isRTL ? (f.nameSindhi || f.name) : f.name}
                      </label>
                    ))}
                  </div>
                </div>

                {/* 8. Literary Services */}
                <div className="form-group">
                  <label className="form-label">{isRTL ? 'ادبي خدمتون (انگريزي)' : 'Literary Services (English)'}</label>
                  <textarea className="form-input" rows={3} placeholder="Published works, awards..." value={form.literary_services} onChange={e => set('literary_services', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={sindhiInput}>{isRTL ? 'ادبي خدمتون (سنڌي)' : 'Literary Services (Sindhi)'}</label>
                  <textarea className="form-input" rows={3} style={sindhiInput} placeholder="شايع ٿيل ڪتاب، انعام..." value={form.literary_services_sindhi} onChange={e => set('literary_services_sindhi', e.target.value)} />
                </div>

                {/* 9-10. Addresses */}
                <div className="form-group">
                  <label className="form-label" style={labelStyle}>{isRTL ? 'عارضي پتو' : 'Temporary Address'}</label>
                  <textarea className="form-input" rows={2} value={form.temporary_address} onChange={e => set('temporary_address', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={labelStyle}>{isRTL ? 'مستقل پتو' : 'Permanent Address'}</label>
                  <textarea className="form-input" rows={2} value={form.permanent_address} onChange={e => set('permanent_address', e.target.value)} />
                </div>

                {/* Branch */}
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label" style={labelStyle}>{isRTL ? 'شاخ چونڊيو' : 'Choose Branch'} *</label>
                  <select className="form-input form-select" value={form.branchId} onChange={e => set('branchId', e.target.value)} required>
                    <option value="">{isRTL ? '-- شاخ چونڊيو --' : '-- Select Branch --'}</option>
                    {branches.map(b => <option key={b._id} value={b._id}>{isRTL ? (b.nameSindhi || b.name) : b.name} — {isRTL ? (b.cityVillageSindhi || b.cityVillage || '') : b.cityVillage || ''}</option>)}
                  </select>
                </div>

                {/* Phone, Email, Password */}
                <div className="form-group">
                  <label className="form-label" style={labelStyle}>{isRTL ? 'فون' : 'Phone'} *</label>
                  <input className="form-input" placeholder="+92-3XX-XXXXXXX" value={form.phone} onChange={e => set('phone', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="form-label">{isRTL ? 'اي ميل' : 'Email'} *</label>
                  <input className="form-input" type="email" placeholder="email@example.com" value={form.email} onChange={e => set('email', e.target.value)} required />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label" style={labelStyle}>{isRTL ? 'پاسورڊ' : 'Password'} *</label>
                  <input className="form-input" type="password" placeholder="••••••••" value={form.password} onChange={e => set('password', e.target.value)} required minLength={8} />
                </div>
              </div>

              {/* Agreement Checkbox */}
              <div style={{ background: 'var(--sindhi-ivory)', borderRadius: 16, padding: '20px 24px', margin: '32px 0', display: 'flex', gap: 16, alignItems: 'flex-start', direction: isRTL ? 'rtl' : 'ltr', border: '1px solid var(--sindhi-cream)' }}>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 4, width: 20, height: 20, accentColor: 'var(--sindhi-maroon)', flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.6, color: 'var(--sindhi-navy)', margin: 0, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', textAlign: isRTL ? 'right' : 'left' }}>
                    {isRTL
                      ? 'آئون سنڌي ادبي سنگت جي متن ۽ مقصدن سان شامل راء آهيان ۽ ان جو/جي ميمبر ٿيان ٿو/ٿي ۽ ان جي في ادا ڪريان ٿو/ ٿي.'
                      : 'I agree with the constitution and objectives of Sindhi Adabi Sangat, and I become a member of it and pay its fee.'}
                  </p>
                </div>
              </div>

              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="btn btn-primary w-100" disabled={loading || !agreed}
                style={{ opacity: (loading || !agreed) ? 0.6 : 1, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                {loading ? (isRTL ? 'جمع ٿي رهيو...' : 'Submitting...') : (isRTL ? 'رجسٽريشن جمع ڪريو' : 'Submit Registration')}
              </motion.button>

              <div style={{ textAlign: 'center', marginTop: 32, fontSize: 15, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                {isRTL ? 'اڳ ۾ ئي کاتو آهي؟' : 'Already have an account?'}{' '}
                <Link to="/login" style={{ color: 'var(--sindhi-maroon)', fontWeight: 800, textDecoration: 'none' }}>{isRTL ? 'داخل ٿيو' : 'Sign In'}</Link>
              </div>
            </form>
          </Card>
        </div>
        <Footer />
      </div>

      {/* Success Modal with Recovery Key */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(10,25,47,0.85)', backdropFilter: 'blur(10px)',
          padding: 20
        }}>
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{
            background: 'white', maxWidth: 500, width: '100%', borderRadius: 32,
            padding: 40, textAlign: 'center', boxShadow: '0 50px 100px -20px rgba(0,0,0,0.5)',
            border: '1px solid var(--sindhi-gold)', position: 'relative', overflow: 'hidden'
          }}>
            {/* Background Pattern */}
            <div style={{ position: 'absolute', inset: 0, opacity: 0.05, backgroundImage: `url(${ajrakTexture})`, backgroundSize: '200px', pointerEvents: 'none' }} />
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ width: 80, height: 80, background: 'var(--sindhi-gold)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, margin: '0 auto 24px', boxShadow: '0 10px 20px rgba(184,134,11,0.3)' }}>✓</div>
              
              <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: 28, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 16 }}>
                {t('registrationSuccess')}
              </h2>
              
              <p style={{ fontSize: 15, color: 'var(--sindhi-text-muted)', marginBottom: 24, lineHeight: 1.6, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                {t('recoveryKeyNote')}
              </p>
              
              <div style={{ background: 'var(--sindhi-ivory)', border: '2px dashed var(--sindhi-gold)', borderRadius: 20, padding: '20px', marginBottom: 32, position: 'relative' }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sindhi-gold)', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>{t('recoveryKey')}</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--sindhi-navy)', letterSpacing: 3, fontFamily: 'monospace' }}>{registeredKey}</div>
                <button onClick={() => { navigator.clipboard.writeText(registeredKey); toast.success('Copied!'); }} style={{ position: 'absolute', right: 12, top: 12, background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>📋</button>
              </div>
              
              <button onClick={() => navigate('/login')} className="btn btn-primary w-100" style={{ padding: '16px', fontSize: 16, fontWeight: 700 }}>
                {isRTL ? 'داخل ٿيو' : 'Proceed to Login'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}